import { useState, useEffect, useCallback } from 'react';
import { useSignedS3Url } from './useSignedS3Url';
import characterImage from '../assets/shutterstock_2294275225-480x480.jpg';

// Same URL expiration time as the original
const URL_EXPIRATION_TIME = 15 * 60 * 1000;

/**
 * Custom hook that uses the new useSignedS3Url hook to fetch and cache signed URLs with expiration handling
 * This is a drop-in replacement for useReduxSignedUrlV2
 * 
 * @param {string} url - The original URL
 * @param {string} userId - The user ID for authentication
 * @returns {Object} - { signedUrl, isLoading, isInCache, refreshUrl, handleImageError }
 */
const useReduxSignedUrlV2 = (url, userId) => {
  const { getSignedUrl } = useSignedS3Url();
  const [signedUrl, setSignedUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [lastFetchTime, setLastFetchTime] = useState(Date.now());
  const [hasError, setHasError] = useState(false);
  const [finalUrl, setFinalUrl] = useState(url);

  // Function to refresh the signed URL
  const refreshUrl = useCallback(async () => {
    if (url && userId) {
      setIsLoading(true);
      try {
        const result = await getSignedUrl(url, userId);
        if (result) {
          setSignedUrl(result);
          setHasError(false);
        }
      } catch (error) {
        console.error('Error refreshing signed URL:', error);
        setHasError(true);
      } finally {
        setIsLoading(false);
        setLastFetchTime(Date.now());
      }
    }
  }, [getSignedUrl, url, userId]);

  // Initial fetch of signed URL
  useEffect(() => {
    if (!signedUrl && url && userId) {
      refreshUrl();
    } else if (signedUrl) {
      setFinalUrl(signedUrl);
    }
  }, [url, userId, signedUrl, refreshUrl]);

  // Check if the URL has expired and refresh if needed
  useEffect(() => {
    const now = Date.now();
    const isExpired = now - lastFetchTime > URL_EXPIRATION_TIME;

    if (signedUrl && isExpired) {
      console.log('Signed URL has expired, refreshing:', url);
      refreshUrl();
    }
  }, [signedUrl, lastFetchTime, refreshUrl, url]);

  // Handle image loading errors
  const handleImageError = useCallback(() => {
    console.warn('Image failed to load:', finalUrl);
    setHasError(true);

    // If the signed URL failed to load and hasn't been refreshed recently, try refreshing
    const now = Date.now();
    const recentlyRefreshed = now - lastFetchTime < 10000; // 10 seconds

    if (!recentlyRefreshed && url && userId) {
      console.log('Attempting to refresh signed URL after load failure');
      refreshUrl();
    }
  }, [finalUrl, lastFetchTime, refreshUrl, url, userId]);

  // Determine the final URL to use
  useEffect(() => {
    if (hasError) {
      // If there was an error loading the image, use the default image
      setFinalUrl(characterImage);
    } else if (signedUrl) {
      // If we have a signed URL, use it
      setFinalUrl(signedUrl);
    } else if (url) {
      // Otherwise fall back to the original URL
      setFinalUrl(url);
    } else {
      // If no URL is provided, use the default image
      setFinalUrl(characterImage);
    }
  }, [signedUrl, url, hasError]);

  return {
    signedUrl: finalUrl,
    isLoading,
    isInCache: !!signedUrl,
    refreshUrl,
    handleImageError
  };
};

export default useReduxSignedUrlV2;
